import {
  COMPANY_COUNTRY_OPTIONS,
  CONTENT_MEDIA_TYPES,
  CONTENT_TEXT_TYPES,
  FLOW_TYPES,
  LANGUAGE_OPTIONS,
  SERVICE_NAMES,
  SERVICE_TYPES,
} from '@/helpers/enums';
import { FITNESS_WORDING_EN } from '@/wording/fitness/FITNESS_WORDING_EN';

export interface TagProps {
  tag?:
    | typeof FITNESS_WORDING_EN.text_media_1
    | typeof FITNESS_WORDING_EN.text_media_2;
}

export interface CardDefinition {
  id: string;
  timestamp: number;
  tags: string[];
  name: string;
  imageSrc: string;
  type: ContentType;
  serviceType?: ServiceType; // used for multi services text contents
}

export type ContentType = keyof ServiceData;
export type TextNumber = 'text1' | 'text2'; // Folder/Urls form text content
export type FlowType = (typeof FLOW_TYPES)[keyof typeof FLOW_TYPES]; // Flow type and used in S3 path

export type LegalsProps = {
  serviceUrlLink: string;
  language: LanguageOptions;
  fileName: 'terms' | 'privacy';
  flowType?: FlowType;
  legalsDynamicS3PathFromServiceUrl?: boolean;
};

export type ContentItem = {
  id: string | number;
  title: string;
  tags: string[];
};

export type ServiceData = {
  articles?: ContentItem[];
  exercises?: ContentItem[];
  recipes?: ContentItem[];
  audios?: ContentItem[];
  guides?: ContentItem[];
  videos?: ContentItem[];
};

export type LanguageOptions = Exclude<keyof typeof LANGUAGE_OPTIONS, 'ALL'>;

export type CompanyCountryOption =
  (typeof COMPANY_COUNTRY_OPTIONS)[keyof typeof COMPANY_COUNTRY_OPTIONS];

export type CompanyCountryWording = Record<
  CompanyCountryOption,
  Record<LanguageOptions, string>
>;

export type Company = {
  COMPANY_NAME: string;
  COMPANY_COUNTRY: CompanyCountryOption;
  COMPANY_ADDRESS: string;
  REGISTRATION_NUMBER: string;
  UTM_SOURCE: string;
  CONTACT?: string; // use in Impressum
  TVA?: string; // use in Impressum
};

export type ServiceType = (typeof SERVICE_TYPES)[keyof typeof SERVICE_TYPES];

export type ContentMediaType =
  (typeof CONTENT_MEDIA_TYPES)[keyof typeof CONTENT_MEDIA_TYPES];

export type ContentTextType =
  (typeof CONTENT_TEXT_TYPES)[keyof typeof CONTENT_TEXT_TYPES];

type ServiceWordingMap = {
  [service in ServiceType]: {
    [lang in LanguageOptions]?: Wording;
  };
};

type MultiFitnessYogaPilatesWordingMap = {
  multiFitnessYogaPilates: {
    [lang in LanguageOptions]?: WordingMultiFitnessYogaPilates;
  };
};

type MultiAstrologyMeditationLoveWordingMap = {
  multiAstrologyMeditationLove?: {
    [lang in LanguageOptions]?: WordingMultiAstrologyMeditationLove;
  };
};

export type GlobalWordingMap = ServiceWordingMap &
  MultiFitnessYogaPilatesWordingMap &
  MultiAstrologyMeditationLoveWordingMap;

// All the theme available
export type ThemesOptions =
  | 'fitness'
  | 'pilates'
  | 'yoga'
  | 'meditation'
  | 'astrology'
  | 'love'
  | 'multiFitnessYogaPilates'
  | 'multiAstrologyMeditationLove'
  | 'cooking';

// All the style require in theme
export type ThemeStyle = {
  // Put an empty string for the default style or use an hex value
  backgroundColor: string; // Most of the app, should always we #FFFFFF
  backgroundColorSecondary: string; // Some sections cards, tags default, notes
  backgroundColorFooter: string; // Only use in the footer
  accentColorOne: string; // Button Hero, button Resources, section recipes button and card bg, links terms and privacy
  accentColorTwo: string; // Sections titles, cards links and dates, filters item selected, footer service name
  textColorDark: string; // Used with light background
  textColorDarkSecondary: string; // Used in sections span under subheader
  textColorLight: string; // Used with dark background
  textColorLightSecondary: string; // Most of Footer text
  borderColor: string; // Cards border, Notes separator
  gradientColorStart: string; // Used for the gradient in Resources section (Top)
  gradientColorEnd: string; // Used for the gradient in Resources section (Bottom)
  playButtonCircle: string; // Used in the svg code of the play button
  playButtonTriangle: string; // Used in the svg code of the play button
  tagBgColor?: string; // Background color of tags
  logoSrc: string; // S3 url for the logo
  faviconSrc: string; // S3 url for favicon
  appleTouchIconSrc: string; // S3 url for Apple touch icon
};

export type Config = {
  SERVICE_NAME: keyof typeof SERVICE_NAMES; // S3 folder + used in components/pages + metadata
  // --- Company informations start
  // Data are in `configs/companies.ts`
  COMPANY_NAME: string; // Used in terms & privacy & Impressum
  COMPANY_COUNTRY: CompanyCountryOption;
  COMPANY_ADDRESS: string; // Used in terms & privacy & Impressum
  REGISTRATION_NUMBER: string; // Used in terms & privacy & Impressum
  CONTACT?: string; // Used in Impressum
  TVA?: string; // Used in Impressum
  UTM_SOURCE: string; // Used in config to dynamicly insert utm_source in AUTH_REDIRECT_URL
  DISABLE_UPDATE_UTM_SOURCE?: boolean; // When true, utm_source is not auto-updated in AUTH_REDIRECT_URL base on COMPANY
  // --- Company informations end
  FLOW_TYPE?: FlowType; // Used to resolve shared legal files from S3. Should be set for every service config.
  languages: LanguageOptions[]; // Used to define languages options for the service, default language is the first one
  serviceType: ServiceType; // Current/active service used by the app once the config has been built. In multi-service portals this is still required at runtime because it drives the currently selected service, base wording fallback, and some service-specific assets/links.
  /** The selected multi-service portal preset, if any. */
  multiService?: MultiServicePreset;
  /** Services belonging to `multiService`, in the preset's stable order. */
  multiServiceTypes?: ServiceType[];
  mediaFilesLanguage?: LanguageOptions; // Optional override for the language used only for audio/video file paths on S3. Wording, JSON data, and markdown descriptions still follow the selected language.
  theme: ThemeStyle;
  SERVICE_COUNTRY: string | Partial<Record<LanguageOptions, string>>;
  CUSTOMER_CARE_PHONE_NUMBER: string;
  KEYWORD?: string; // Only for sms
  SHORT_CODE?: string; // Only for sms
  SERVICE_PRICE: string | Partial<Record<LanguageOptions, string>>;
  SERVICE_DESCRIPTION_METADATA?:
    | string
    | Partial<Record<LanguageOptions, string>>; // If provided, this value takes priority over the default hardcoded translation (`hero_text`). It can be a single string or a map by language. Otherwise, the description will fall back to the `hero_text` from the first language listed in the `languages` array.
  SERVICE_CONTACT_EMAIL: string; // Used in components/pages
  SERVICE_URL_LINK: string; // Used in components/pages
  AUTH_REDIRECT_URL: string; // Used for redirection if user isn't authenticated
  LEGALS_DYNAMIC_S3_PATH_FROM_SERVICE_URL?: boolean; // When true, legal files use the service-specific S3 path instead of the shared path.
  TERMS_URL?: string; // Used instead of the dynamic terms url when it's needed (e.g, a service in a language that need legals in another language)
  PRIVACY_URL?: string; // Used instead of the dynamic privacy url when it's needed (e.g, a service in a language that need legals in another language)
  TERMS_22_KW_AND_SC_REPLACEMENT?:
    | string
    | Partial<Record<LanguageOptions, string>>; // Used in terms to replace 2.2 text about Keyword and Shortcode
  GOOGLE_VERIFICATION?: string; // Used to verify ownership of domaine for google ads
  contents: {
    media: ContentMediaType; // Used display media related contents/components, build S3 url and api url
    media_multilingual_storage?: boolean; // Used to build S3 url path for media files. If there is sub folders for languages or not (Fitness/Yoga/Pilates/Meditation/Cooking is set to false, Astro/Love is set to true)
    text1: ContentTextType | null; // Used display text1 related contents/components and build S3 url (historically "recipes" for fitness...)
    text2: ContentTextType | null; // Used display text2 related contents/components and build S3 url (historically "blog" or "articles" for fitness...)
  };
};

export type ConfigContents = Config['contents'];

export type ConfigBase = Omit<
  Config,
  | 'serviceType'
  | 'multiService'
  | 'multiServiceTypes'
  | 'theme'
  | 'contents'
  | 'SERVICE_URL_LINK'
> & {
  // Optional in raw host config definitions.
  // At runtime the app still needs a concrete serviceType to know which
  // service is currently active in a multi-service portal.
  serviceType?: Config['serviceType'];
  // `undefined` uses the SERVICE_NAME profile; `null` explicitly disables it.
  multiService?: Config['multiService'] | null;
  SERVICE_URL_LINK?: Config['SERVICE_URL_LINK'];
  theme?: ThemeStyle;
  contents?: Config['contents'];
};

export type ConfigBaseMap = Record<string, ConfigBase>;

type HostMapWithoutTrailingSlash<Hosts extends Record<string, unknown>> = {
  [Host in keyof Hosts]: Host extends `${string}/` ? never : Hosts[Host];
};

/**
 * Declares a host map while preserving its literal keys. Host keys are used for
 * exact lookups, so they must not end with a `/`.
 */
export const defineHostMap = <const Hosts extends Record<string, unknown>>(
  hosts: Hosts & HostMapWithoutTrailingSlash<Hosts>,
): Hosts => hosts;

type ServiceNamesByServiceType = {
  [SERVICE_TYPES.FITNESS]:
    | typeof SERVICE_NAMES.MyFitGoal
    | typeof SERVICE_NAMES.FitFlowClub;
  [SERVICE_TYPES.YOGA]:
    | typeof SERVICE_NAMES.ZenZone
    | typeof SERVICE_NAMES.BodyZenMotion;
  [SERVICE_TYPES.PILATES]:
    | typeof SERVICE_NAMES.MyPilatesCoach
    | typeof SERVICE_NAMES.PilatesHorizon;
  [SERVICE_TYPES.COOKING]: typeof SERVICE_NAMES.MyChef;
  [SERVICE_TYPES.ASTROLOGY]: typeof SERVICE_NAMES.Astrolia;
  [SERVICE_TYPES.LOVE]: typeof SERVICE_NAMES.Lovara;
  [SERVICE_TYPES.MEDITATION]: typeof SERVICE_NAMES.Medisoul;
};

type ServiceHostConfigBase<T extends ServiceType> = Omit<
  ConfigBase,
  'SERVICE_NAME' | 'serviceType' | 'multiService'
> & {
  SERVICE_NAME: ServiceNamesByServiceType[T];
  serviceType?: T;
  multiService?: never;
};

type ExplicitServiceNameOverridesByServiceType = {
  // Apptitude's shared FYP profile is explicitly disabled for these mono-Fitness hosts.
  [SERVICE_TYPES.FITNESS]: typeof SERVICE_NAMES.Apptitude;
};

type ExplicitServiceTypeOverrideHostConfigBase<T extends ServiceType> =
  T extends keyof ExplicitServiceNameOverridesByServiceType
    ? Omit<ConfigBase, 'SERVICE_NAME' | 'serviceType' | 'multiService'> & {
        SERVICE_NAME: ExplicitServiceNameOverridesByServiceType[T];
        serviceType: T;
        multiService: null;
      }
    : never;

/**
 * Host configs for a single service type, limited to its matching service names
 * and explicitly declared single-service overrides.
 */
export type HostConfigMapForServiceType<T extends ServiceType> = Record<
  string,
  ServiceHostConfigBase<T> | ExplicitServiceTypeOverrideHostConfigBase<T>
>;

/** Host configs constrained to a known set of service names. */
export type HostConfigMapForServiceNames<
  Names extends Config['SERVICE_NAME'],
> = Record<
  string,
  Omit<ConfigBase, 'SERVICE_NAME'> & { SERVICE_NAME: Names }
>;

export enum MultiServicePreset {
  FYP = 'FYP',
  AML = 'AML',
}

export interface Wording {
  // Shared
  button_load_more: string;
  page_media_content_menu_all?: string;
  page_media_content_menu_biceps?: string;
  page_media_content_menu_triceps?: string;
  page_media_content_menu_back?: string;
  page_media_content_menu_chest?: string;
  page_media_content_menu_legs?: string;
  page_media_content_menu_shoulders?: string;
  page_media_content_menu_abdominals?: string;
  page_media_content_menu_antistress?: string;
  page_media_content_menu_mindfulness?: string;
  page_media_content_menu_energyboost?: string;
  page_media_content_menu_mantra?: string;
  page_media_content_menu_foundations?: string;
  page_media_content_menu_spirituality?: string;
  page_media_content_menu_tarot?: string;
  page_media_content_menu_psychology?: string;
  page_media_content_menu_trends?: string;
  page_media_content_menu_compatibility?: string;
  page_media_content_menu_love?: string;
  text_media_1: string;
  text_media_2: string;

  // Component Card
  components_card_link_media_content: string;
  components_card_link_text1_content: string;
  components_card_link_text2_content: string;

  // Component Footer
  components_footer_text: string;
  components_footer_info: string;
  components_footer_registration_number: string;
  components_footer_legal: string;
  components_footer_terms: string;
  components_footer_privacy: string;

  // Component Header
  components_header_media_content: string;
  components_header_media_1: string;
  components_header_media_2?: string;
  components_header_text1: string;
  components_header_text2: string;
  components_header_members?: string; // Old, not used anymore

  // Component LoginSuccess
  components_login_success_text: string;

  // Component NotesAndGoogle
  components_notes_and_google_title_1: string;
  components_notes_and_google_title_2: string;
  components_notes_and_google_title_3: string;

  // Component ReviewGoogle
  components_review_google_text: string;

  // Component TextOneSection
  components_text1_section_title: string;
  components_text1_subheader_text: string;
  components_text1_span_text: string;
  components_text1_card_1_title: string;
  components_text1_card_1_text: string;
  components_text1_card_2_title: string;
  components_text1_card_2_text: string;
  components_text1_card_3_title: string;
  components_text1_card_3_text: string;
  components_text1_link: string;

  // Component Ressources
  components_ressources_section_title: string;
  components_ressources_subheader_text: string;
  components_ressources_span_text: string;
  components_ressources_card_1_title: string;
  components_ressources_card_1_text: string;
  components_ressources_card_2_title: string;
  components_ressources_card_2_text: string;
  components_ressources_link: string;

  // Page Text2
  page_text2_hero_text: string;
  page_text2_section_title: string;
  page_text2_subheader_text: string;

  // Page Text1
  page_text1_hero_text: string;
  page_text1_section_title: string;
  page_text1_subheader_text: string;

  // Pages MediaContent
  page_media_1_content_hero_text: string;
  page_media_2_content_hero_text?: string;

  // TextTwoSection
  text2_section_title: string;
  text2_section_subheader_text: string;
  text2_section_button_more: string;

  // Hero
  hero_text: string;
  hero_button_text: string;

  // MediaContentSection
  media_content_section_title: string;
  media_content_section_subheader_text: string;
  media_content_section_button_more: string;

  // Enter Email
  // Old "account" page but wasn't used
  enter_email_title?: string;
  enter_email_placeholder_email?: string;
  enter_email_button_enter?: string;

  // Companies countries
  // Not used anymore, we use an enum and object instead
  france?: string;
  cyprus?: string;

  // Tags for "All" when there is only one media type
  tag_podcast?: string;
  tag_meditation?: string;
}

export interface WordingMultiFitnessYogaPilates {
  // ---------------- Hero ----------------
  hero_text: string;
  hero_button_text: string;

  // ---------------- Resources Section ----------------
  components_ressources_subheader_text: string;
  components_ressources_span_text: string;
  components_ressources_card_1_title: string;
  components_ressources_card_1_text: string;
  components_ressources_card_2_title: string;
  components_ressources_card_2_text: string;

  // ---------------- Nav ----------------
  components_header_media_content: string;
  components_header_media_1: string;
  components_header_media_2: string;
  components_header_media_multi_content_2: string; // new
  components_header_media_multi_content_3: string; // new

  // ---------------- NotesAndGoogle ----------------
  components_notes_and_google_title_1: string;
  components_notes_and_google_title_2: string;
  components_notes_and_google_title_3: string;

  // ---------------- Media Section ----------------
  media_content_section_multi_1_subheader_text: string; // new
  media_content_section_multi_2_subheader_text: string; // new
  media_content_section_multi_3_subheader_text: string; // new
  text_media_1: string;
  text_media_2: string;
  page_media_content_menu_biceps: string;
  page_media_content_menu_triceps: string;
  page_media_content_menu_back: string;
  page_media_content_menu_chest: string;
  page_media_content_menu_legs: string;
  page_media_content_menu_shoulders: string;
  page_media_content_menu_abdominals: string;

  // ---------------- Text One Section ----------------
  components_text1_subheader_text: string;
  components_text1_span_text: string;

  // ---------------- Footer ----------------
  components_footer_text: string;
}

export interface WordingMultiAstrologyMeditationLove extends Partial<Wording> {
  // ---------------- Hero ----------------
  hero_text: string;
  hero_button_text: string;

  // ---------------- Resources Section ----------------
  components_ressources_subheader_text: string;
  components_ressources_span_text: string;
  components_ressources_card_1_title: string;
  components_ressources_card_1_text: string;
  components_ressources_card_2_title: string;
  components_ressources_card_2_text: string;

  // ---------------- Nav ----------------
  components_header_media_content: string;
  components_header_media_multi_content_2: string;
  components_header_media_multi_content_3: string;
  components_header_text1: string;
  components_header_text2: string;

  // ---------------- NotesAndGoogle ----------------
  components_notes_and_google_title_1: string;
  components_notes_and_google_title_2: string;
  components_notes_and_google_title_3: string;

  // ---------------- Media Section ----------------
  media_content_section_multi_1_subheader_text: string;
  media_content_section_multi_2_subheader_text: string;
  media_content_section_multi_3_subheader_text: string;
  page_media_content_menu_foundations?: string;
  page_media_content_menu_spirituality?: string;
  page_media_content_menu_tarot?: string;
  page_media_content_menu_psychology?: string;
  page_media_content_menu_trends?: string;
  page_media_content_menu_antistress?: string;
  page_media_content_menu_mindfulness?: string;
  page_media_content_menu_energyboost?: string;
  page_media_content_menu_mantra?: string;
  page_media_content_menu_compatibility?: string;
  page_media_content_menu_love?: string;
  tag_podcast?: string;
  tag_meditation?: string;

  // ---------------- Text Sections ----------------
  text2_section_title: string;
  components_text1_subheader_text: string;
  components_text1_span_text: string;
  text2_section_subheader_text: string;
  text2_section_button_more: string;
  components_text1_section_title: string;
  components_text1_card_1_title: string;
  components_text1_card_1_text: string;
  components_text1_card_2_title: string;
  components_text1_card_2_text: string;
  components_text1_card_3_title: string;
  components_text1_card_3_text: string;
  components_text1_link: string;

  // ---------------- Footer ----------------
  components_footer_text: string;
}

export type UnsubscribeWording = {
  successMessage: string;
  errorMessage: string;
  pageTitle: string;
  phoneInputLabel: string;
  stopInstruction: string;
  confirmButtonLabel: string;
};

export type WithdrawalWording = {
  badgeLabel: string;
  pageTitle: string;
  description: string;
  serviceLabel: string;
  serviceUrlLabel: string;
  phoneInputLabel: string;
  phoneInputPlaceholder: string;
  emailInputLabel: string;
  emailInputPlaceholder: string;
  commentInputLabel: string;
  commentInputPlaceholder: string;
  submitButtonLabel: string;
  successMessage: string;
  errorMessage: string;
  autogeneratedEmailNotice: string;
};
